/**
 * @cfleet/api-client - Addon Management Hooks
 *
 * React Query hooks for addon management operations in the dispatcher app.
 * These endpoints require authentication and are scoped to the current organization.
 *
 * @example
 * ```tsx
 * import { useAddons, useToggleAddon } from '@cfleet/api-client';
 *
 * // List available addons
 * const { data, isLoading, error } = useAddons({ authConfig });
 *
 * // Toggle an addon's installation state
 * const { mutate: toggleAddon, isPending } = useToggleAddon({ authConfig });
 * toggleAddon({ addonId: 'addon_maintenance' });
 * ```
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AnalyticsComponents } from '../generated';
import { createAuthenticatedCoreClient, type AuthMiddlewareConfig } from '../index';

// ─────────────────────────────────────────────────────────────────────────────
// Type Aliases
// ─────────────────────────────────────────────────────────────────────────────

type Addon = AnalyticsComponents['schemas']['Addon'];
type AddonsListResponse = AnalyticsComponents['schemas']['AddonsListResponse'];
type AddonToggleResponse = AnalyticsComponents['schemas']['AddonToggleResponse'];

// ─────────────────────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Configuration for addon hooks
 */
export interface UseAddonHooksConfig {
  /** Authentication middleware configuration (required) */
  authConfig: AuthMiddlewareConfig;
}

// ─────────────────────────────────────────────────────────────────────────────
// useAddons
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to list all available addons for the current organization.
 *
 * Returns addon metadata including installation status, pricing, and display info.
 * The `installed` field indicates whether each addon is enabled for the organization.
 *
 * @param config - Configuration including auth config
 * @returns Query hook with addon list
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useAddons({ authConfig });
 *
 * if (isLoading) return <div>Loading addons...</div>;
 *
 * return (
 *   <AddonMarketplace
 *     addons={data?.addons ?? []}
 *     installedCount={data?.installed_count ?? 0}
 *   />
 * );
 * ```
 */
export function useAddons(config?: UseAddonHooksConfig) {
  return useQuery({
    queryKey: ['addons'],
    queryFn: async (): Promise<AddonsListResponse> => {
      if (!config?.authConfig) {
        throw new Error('Auth config required');
      }

      const client = createAuthenticatedCoreClient(config.authConfig);
      const { data, error } = await client.GET('/settings/addons');

      if (error) {
        throw new Error(error.detail || 'Failed to fetch addons');
      }

      return data!;
    },
    enabled: !!config?.authConfig,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useToggleAddon
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to toggle an addon's installation state.
 *
 * Supports optimistic updates: the UI reflects the change immediately,
 * then rolls back if the server request fails. This provides better UX
 * for install/uninstall operations.
 *
 * @param config - Configuration including auth config
 * @returns Mutation hook for toggling addon state
 *
 * @example
 * ```tsx
 * const { mutate: toggleAddon, isPending, error } = useToggleAddon({ authConfig });
 *
 * const handleToggle = (addonId: string) => {
 *   toggleAddon({ addonId }, {
 *     onSuccess: () => {
 *       toast.success('Addon updated');
 *     },
 *     onError: (error) => {
 *       toast.error(error.message);
 *     },
 *   });
 * };
 * ```
 */
export function useToggleAddon(config?: UseAddonHooksConfig) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ addonId }: { addonId: string }): Promise<AddonToggleResponse> => {
      if (!config?.authConfig) {
        throw new Error('Auth config required');
      }

      const client = createAuthenticatedCoreClient(config.authConfig);
      const { data, error } = await client.POST('/settings/addons/{id}/toggle', {
        params: { path: { id: addonId } },
      });

      if (error) {
        throw new Error(error.detail || 'Failed to toggle addon');
      }

      return data!;
    },

    onMutate: async ({ addonId }) => {
      // Cancel any outgoing refetches so they don't overwrite optimistic update
      await queryClient.cancelQueries({ queryKey: ['addons'] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<AddonsListResponse>(['addons']);

      // Optimistically update the addons list
      if (previousData?.addons) {
        queryClient.setQueryData<AddonsListResponse>(['addons'], {
          ...previousData,
          addons: previousData.addons.map((addon: Addon) =>
            addon.id === addonId ? { ...addon, installed: !addon.installed } : addon,
          ),
        });
      }

      return { previousData };
    },

    onError: (_, __, context) => {
      // Rollback to previous data on error
      if (context?.previousData) {
        queryClient.setQueryData(['addons'], context.previousData);
      }
    },

    onSuccess: () => {
      // Invalidate addons query to ensure we have fresh server state
      queryClient.invalidateQueries({ queryKey: ['addons'] });
    },
  });
}
